import { Map } from 'immutable'
import 'intersection-observer'
import * as React from 'react'
import { InView } from 'react-intersection-observer'
import { AsyncState, fromJSX, IOWidget, Updater } from 'widgets-for-react'
import { blockInviewHandler } from '../../../drupal/rendering/inview_utils.mod'
import { ImageSize } from '../types.gen'
import { getClassName } from '../utils.gen'
import { Media } from '../utils/media.mod'
import { ImageContainer } from './ImageContainer.gen'
import { CMSState } from '../../../publicSite/drupalPage/state'

export type MediaBlockNestedProps = {
  index: number
  items: MediaItemNestedProps[]
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type MediaItemNestedProps = {
  kind: 'image' | 'video'
  image?: number
  image_alt?: string
  size?: ImageSize
  get_default_data_src: () => ImageSize
  get_default_data_srcset: () => [ImageSize, string][]
  video?: { url: string } & {
    id: string
    type: string
    width: string
    height: string
    frameBorder: string
    allowFullScreen: boolean
  }
  caption?: string
  source?: string
  useCookieBar: boolean
  cookiePlaceholder: JSX.Element | string
  cookieButton: string
}

export type MediaBlockNestedStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  content_classname: string
  figure_classname: string
  figure_image_classname: string
  figure_video_classname: string
  media_classname: string
  image_classname: string
  picture_classname: string
  video_classname: string
  figcaption_classname: string
  caption_classname: string
  source_classname: string
  cookies_classname: string
  cookies_inner_classname: string
  cookies_text_classname: string
  cookies_clickable_classname: string
}>

export function MediaBlockNested(
  props: MediaBlockNestedProps,
  options?: { style?: MediaBlockNestedStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return (cmsState: CMSState) =>
    fromJSX((_) => {
      const MediaItemNested = (item: MediaItemNestedProps, index: number) => {
        return (
          <figure
            className={
              getClassName(options, 'figure_classname', '') +
              ' ' +
              getClassName(options, item.kind === 'image' ? 'figure_image_classname' : 'figure_video_classname', '')
            }
            key={`media-${options && options.key ? options.key : props.index}-${index}`}
          >
            {item.kind === 'image'
              ? item.image != null &&
                cmsState.images.has(item.image) && (
                  <div className={getClassName(options, 'media_classname', '')}>
                    {ImageContainer(
                      cmsState.images.get(item.image) as AsyncState<Map<ImageSize, string>>,
                      item.size,
                      getClassName(options, 'image_classname', 'w-100'),
                      item.image_alt,
                      item.get_default_data_src,
                      item.get_default_data_srcset
                    ).run((_) => {})}
                  </div>
                )
              : item.kind === 'video' &&
                item.video != undefined && (
                  <div className={getClassName(options, 'media_classname', 'text-center')}>
                    <Media kind="media" useCookiebar={item.useCookieBar} url={item.video.url} />
                  </div>
                )}

            {(item.caption || item.source) && (
              <figcaption className={getClassName(options, 'figcaption_classname', 'mt-2')}>
                {item.caption && (
                  <p className={getClassName(options, 'caption_classname', 'small text-secondary text-center m-0')}>
                    {item.caption}
                  </p>
                )}
                {item.source && (
                  <p className={getClassName(options, 'source_classname', 'small text-secondary text-center m-0')}>
                    {item.source}
                  </p>
                )}
              </figcaption>
            )}
          </figure>
        )
      }

      return (
        <InView
          as="div"
          onChange={(inView) =>
            blockInviewHandler(
              'media',
              inView,
              `io-${options && options.key ? options.key : props.index}`,
              getClassName(options, 'inview_classname', 'inview')
            )
          }
          triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
          threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
          id={`io-${options && options.key ? options.key : props.index}`}
          className={getClassName(options, 'block_classname', '')}
          key={options && options.key ? options.key : `block-${props.index}`}
        >
          <div className={getClassName(options, 'inner_classname', 'py-5')}>
            {props.items && props.items.length > 0 && (
              <div className={getClassName(options, 'content_classname', 'content')}>
                {props.items.map((item: MediaItemNestedProps, index: number) => MediaItemNested(item, index))}
              </div>
            )}
          </div>
        </InView>
      )
    })
}
